import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ChevronLeft, ChevronRight, Upload, CheckCircle, Loader2, Copy, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import apiService, { User, Domain } from '../services/api';
import { DomainsTable } from '../components/DomainsTable';
import { authService } from '../services/authService';

// Define order status options (matching UsersTable)
const ORDER_STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'New Order Received', label: 'New Order Received' },
  { value: 'Not Yet Done with Setup', label: 'Not Yet Done with Setup' },
  { value: 'Setup Done and Ready for Upload', label: 'Setup Done and Ready for Upload' },
  { value: 'Uploaded and Ready to Verify', label: 'Uploaded and Ready to Verify' },
  { value: 'Technical Issue', label: 'Technical Issue' },
  { value: 'Technical Issue Resolved', label: 'Technical Issue Resolved' },
  { value: 'Asks for 2FA', label: 'Asks for 2FA' },
  { value: 'Reconnect Request', label: 'Reconnect Request' },
  { value: 'New Emails Requested', label: 'New Emails Requested' },
];

// Domain status filter options
const DOMAIN_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'all', label: 'All Domains' },
];

export const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [filteredDomains, setFilteredDomains] = useState<Domain[]>([]);
  const [paginatedDomains, setPaginatedDomains] = useState<Domain[]>([]);
  const [userLoading, setUserLoading] = useState(true);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [userError, setUserError] = useState<string | null>(null);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingNotes, setUpdatingNotes] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [clientNotes, setClientNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Get current user for permission checks
  const currentUser = authService.getCurrentUser();

  // Filter states
  const [domainSearch, setDomainSearch] = useState('');
  const [domainStatusFilter, setDomainStatusFilter] = useState('pending'); // Default to pending

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // Verification states
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<any>(null);
  const [liveProgress, setLiveProgress] = useState<{
    currentDomain?: string;
    currentEmail?: string;
    domainsProcessed: number;
    totalDomains: number;
    responses: Array<{
      domain: string;
      email?: string;
      response?: any;
      success: boolean;
      timestamp: string;
    }>;
  }>({
    domainsProcessed: 0,
    totalDomains: 0,
    responses: []
  });
  const [showLiveProgress, setShowLiveProgress] = useState(false);

  // Helper function to calculate total email accounts from all domains
  const calculateTotalEmailAccounts = (): number => {
    return domains.reduce((total, domain) => {
      const accountsToCreate = countEmailsInField(domain.accounts_to_create);
      return total + accountsToCreate;
    }, 0);
  };

  // Helper function to calculate domains created in current month
  const calculateCurrentMonthDomains = (): number => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Create start of current month in EST
    const startOfMonth = new Date(currentYear, currentMonth, 1, 5, 0, 0); // 5 AM UTC = 12 AM EST
    
    return domains.filter(domain => {
      const domainDate = new Date(domain.created_at);
      return domainDate >= startOfMonth;
    }).length;
  };

  // PDF generation function
  const generateInvoicePDF = () => {
    const doc = new jsPDF();
    const totalEmailAccounts = calculateTotalEmailAccounts();
    const currentMonthDomains = calculateCurrentMonthDomains();
    const emailCost = totalEmailAccounts * 4;
    const domainCost = currentMonthDomains * 12;
    const totalCost = emailCost + domainCost;
    
    // Page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Colors (elegant theme)
    const primaryColor = [0, 0, 0]; // Black
    const secondaryColor = [107, 114, 128]; // Gray-500
    const accentColor = [0, 0, 0]; // Black
    const lightGray = [248, 250, 252]; // Gray-50
    const mediumGray = [156, 163, 175]; // Gray-400
    
    // Reset text color for body
    doc.setTextColor(0, 0, 0);
    
    // Company info section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('FROM:', 20, 30);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('Far, LLC', 20, 42);
    doc.text('169 Madison Ave STE 2601', 20, 52);
    doc.text('New York, New York 10016', 20, 62);
    doc.text('United States', 20, 72);
    
    // Invoice details section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('INVOICE DETAILS:', pageWidth - 20, 30, { align: 'right' });
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const invoiceDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const invoiceNumber = `INV-${Date.now()}`;
    doc.text(`Date: ${invoiceDate}`, pageWidth - 20, 42, { align: 'right' });
    doc.text(`Invoice #: ${invoiceNumber}`, pageWidth - 20, 52, { align: 'right' });
    doc.text(`Due: Net 30`, pageWidth - 20, 62, { align: 'right' });
    
    // Bill to section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('BILL TO:', 20, 95);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(user?.email || 'N/A', 20, 107);
    doc.text(`Workspace ID: ${user?.workspace_id || 'N/A'}`, 20, 117);
    doc.text(`Client ID: ${user?.client_id || 'N/A'}`, 20, 127);
    
    // Services table header
    const tableTop = 145;
    const tableHeight = 28;
    
    // Table header background
    doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]); // Gray-50
    doc.rect(20, tableTop, pageWidth - 40, tableHeight, 'F');
    
    // Table border
    doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]); // Gray-400
    doc.setLineWidth(0.5);
    doc.rect(20, tableTop, pageWidth - 40, tableHeight);
    
    // Table headers
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(55, 65, 81); // Gray-700
    doc.text('DESCRIPTION', 25, tableTop + 10);
    doc.text('QTY', 110, tableTop + 10);
    doc.text('RATE', 135, tableTop + 10);
    doc.text('AMOUNT', pageWidth - 25, tableTop + 10, { align: 'right' });
    
    // Line separator
    doc.setLineWidth(0.8);
    doc.line(20, tableTop + tableHeight, pageWidth - 20, tableTop + tableHeight);
    
    let currentY = tableTop + tableHeight + 18;
    
    // Email accounts line item
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Business Workspace License', 25, currentY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Monthly service for email infrastructure', 25, currentY + 10);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(totalEmailAccounts.toString(), 110, currentY);
    doc.text('$4.00', 135, currentY);
    doc.text(`$${emailCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
    
    currentY += 30;
    
    // Domains line item
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Domain DNS Management', 25, currentY);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Annual service for domains (${currentMonth})`, 25, currentY + 10);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(currentMonthDomains.toString(), 110, currentY);
    doc.text('$12.00', 135, currentY);
    doc.text(`$${domainCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
    
    currentY += 35;
    
    // Add some space before subtotal section
    currentY += 20;
    
    // Check if we need a new page (leave space for subtotal and total sections)
    const remainingSpace = pageHeight - currentY;
    const neededSpace = 60; // Reduced space needed for remaining content
    
    if (remainingSpace < neededSpace) {
      doc.addPage();
      currentY = 40; // Reset Y position on new page with some top margin
    }
    
    // Subtotal and total section using dynamic positioning
    doc.setDrawColor(mediumGray[0], mediumGray[1], mediumGray[2]);
    doc.setLineWidth(0.5);
    doc.line(110, currentY, pageWidth - 20, currentY);
    
    currentY += 18;
    
    // Subtotal
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('Subtotal:', 140, currentY);
    doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
    
    currentY += 20;
    
    // Total with black background
    doc.setFillColor(0, 0, 0);
    doc.rect(110, currentY - 10, pageWidth - 130, 25, 'F');
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL:', 140, currentY + 5);
    doc.text(`$${totalCost.toFixed(2)}`, pageWidth - 25, currentY + 5, { align: 'right' });
    
    // Download the PDF
    const filename = `pager-invoice-${user?.workspace_id || 'unknown'}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchDomains();
    }
  }, [userId]);

  // Apply filters whenever domains, search, or status filter changes
  useEffect(() => {
    applyFilters();
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [domains, domainSearch, domainStatusFilter]);

  // Apply pagination whenever filtered domains or pagination page changes
  useEffect(() => {
    applyPagination();
  }, [filteredDomains, pagination.page]);

  const fetchUser = async () => {
    try {
      setUserLoading(true);
      setUserError(null);
      const response = await apiService.getUser(userId!);
      setUser(response.data);
      setClientNotes(response.data.client_notes || '');
      setAdminNotes(response.data.admin_notes || '');
    } catch (err) {
      setUserError('Failed to fetch user details');
      console.error(err);
    } finally {
      setUserLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      setDomainsLoading(true);
      setDomainsError(null);
      const response = await apiService.getUserDomains(userId!);
      setDomains(response.data);
    } catch (err) {
      setDomainsError('Failed to fetch user domains');
      console.error(err);
    } finally {
      setDomainsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...domains];

    // Apply search filter (search in domain name and emails)
    if (domainSearch.trim()) {
      const searchTerm = domainSearch.toLowerCase().trim();
      filtered = filtered.filter(domain => 
        domain.domain_name.toLowerCase().includes(searchTerm) ||
        (domain.emails && domain.emails.toLowerCase().includes(searchTerm))
      );
    }

    // Apply status filter
    if (domainStatusFilter !== 'all') {
      filtered = filtered.filter(domain => {
        // Helper function to check if a value is truly empty/null
        const isEmpty = (value: string | undefined | null) => {
          return !value || 
                 value.trim() === '' || 
                 value.toLowerCase() === 'null' || 
                 value.toLowerCase() === 'undefined';
        };

        switch (domainStatusFilter) {
          case 'pending':
            // Pending: not verified connection AND has accounts to create (emails) but no accounts to cancel AND not canceled
            return !domain.verified_connection && 
                   !isEmpty(domain.accounts_to_create) &&
                   isEmpty(domain.accounts_to_cancel) &&
                   !domain.canceled_domain;
          case 'verified':
            // Verified: verified connection is true
            return domain.verified_connection;
          case 'canceled':
            // Canceled: has accounts to cancel (non-empty) OR has a canceled date OR canceled_domain is true
            return !isEmpty(domain.accounts_to_cancel) || 
                   (!isEmpty(domain.canceled_date) && domain.canceled_date !== null) ||
                   domain.canceled_domain;
          default:
            return true;
        }
      });
    }

    setFilteredDomains(filtered);
    
    // Update pagination totals
    const totalPages = Math.ceil(filtered.length / pagination.limit);
    setPagination(prev => ({
      ...prev,
      total: filtered.length,
      totalPages: totalPages,
    }));
  };

  const applyPagination = () => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    const paginated = filteredDomains.slice(startIndex, endIndex);
    setPaginatedDomains(paginated);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleFilterChange = (type: 'search' | 'status', value: string) => {
    if (type === 'search') {
      setDomainSearch(value);
    } else {
      setDomainStatusFilter(value);
    }
    // Pagination reset is handled in useEffect
  };

  const handleOrderStatusChange = async (newStatus: string) => {
    if (!user || !newStatus) return;
    
    try {
      setUpdatingStatus(true);
      await apiService.updateUserOrderStatus(user.id, newStatus);
      
      // Update the local state
      setUser({ ...user, order_status: newStatus });
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleNotesModalOpen = () => {
    setShowNotesModal(true);
  };

  const handleNotesModalClose = () => {
    setShowNotesModal(false);
    // Reset to original values
    setClientNotes(user?.client_notes || '');
    setAdminNotes(user?.admin_notes || '');
  };

  const handleNotesSave = async () => {
    if (!user) return;
    
    try {
      setUpdatingNotes(true);
      await apiService.updateUserAllNotes(user.id, clientNotes, adminNotes);
      
      // Update the local state
      setUser({ ...user, client_notes: clientNotes, admin_notes: adminNotes });
      setShowNotesModal(false);
    } catch (err) {
      console.error('Failed to save notes:', err);
      alert('Failed to save notes. Please try again.');
    } finally {
      setUpdatingNotes(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to check if domain was created after June 1st, 2025
  const isCreatedAfterJune2025 = (domain: Domain): boolean => {
    if (!domain.created_at) return false;
    const createdDate = new Date(domain.created_at);
    const cutoffDate = new Date('2025-06-01T00:00:00.000Z');
    return createdDate > cutoffDate;
  };

  // Helper function to format password with extra "!" if domain was created after June 1st, 2025
  const formatPassword = (domain: Domain): string => {
    // Check if domain_password exists and is not empty
    const password = domain.domain_password;
    if (!password || password.trim() === '') return '';
    
    const basePassword = password.trim();
    return isCreatedAfterJune2025(domain) ? basePassword + '!' : basePassword;
  };

  // Helper function to check if a value is truly empty/null
  const isEmpty = (value: string | undefined | null) => {
    return !value || 
           value.trim() === '' || 
           value.toLowerCase() === 'null' || 
           value.toLowerCase() === 'undefined';
  };

  // Calculate domain counts from all domains (not filtered)
  const totalDomains = domains.length;
  const pendingDomains = domains.filter(domain => 
    !domain.verified_connection && 
    !isEmpty(domain.accounts_to_create) &&
    isEmpty(domain.accounts_to_cancel) &&
    !domain.canceled_domain
  ).length;

  // Helper function to parse and count emails from a field
  const countEmailsInField = (emailField: string | undefined | null): number => {
    if (!emailField || isEmpty(emailField)) return 0;
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(emailField);
      if (Array.isArray(parsed)) {
        return parsed.filter(email => email && email.trim().includes('@')).length;
      }
      if (typeof parsed === 'object') {
        return Object.values(parsed).filter(v => v && typeof v === 'string' && v.trim().includes('@')).length;
      }
      if (typeof parsed === 'string' && parsed.trim().includes('@')) {
        return 1;
      }
      return 0;
    } catch {
      // If not JSON, try splitting by comma
      return emailField
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0 && email.includes('@')).length;
    }
  };

  // Calculate the requested stats
  const getUserStats = () => {
    let totalEmails = 0;
    let activeDomainsCount = 0;
    let activeEmailsCount = 0;

    domains.forEach(domain => {
      // Count emails in accounts_to_create
      const createEmails = countEmailsInField(domain.accounts_to_create);
      // Count emails in accounts_to_cancel  
      const cancelEmails = countEmailsInField(domain.accounts_to_cancel);
      
      // Total emails = all accounts_to_create + accounts_to_cancel
      totalEmails += createEmails + cancelEmails;
      
      // Active emails = only accounts_to_create emails
      activeEmailsCount += createEmails;
      
      // Active domains = domains where accounts_to_create is not empty
      if (createEmails > 0) {
        activeDomainsCount++;
      }
    });

    return {
      totalDomains,
      totalEmails,
      activeDomainsCount,
      activeEmailsCount
    };
  };

  const userStats = getUserStats();

  // Get the display text for order status
  const getOrderStatusLabel = (status: string) => {
    const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
    return option ? option.label : status || 'Not Set';
  };

  // Get counts for each status
  const getStatusCounts = () => {
    const pending = domains.filter(d => 
      !d.verified_connection && 
      !isEmpty(d.accounts_to_create) &&
      isEmpty(d.accounts_to_cancel) &&
      !d.canceled_domain
    ).length;
    const verified = domains.filter(d => d.verified_connection).length;
    const canceled = domains.filter(d => 
      !isEmpty(d.accounts_to_cancel) || 
      (!isEmpty(d.canceled_date) && d.canceled_date !== null) ||
      d.canceled_domain
    ).length;
    
    return { pending, verified, canceled };
  };

  const statusCounts = getStatusCounts();

  // Handler for oAuth Upload button
  const handleOAuthUpload = async () => {
    if (!userId || isVerifying) return;

    // If Smartlead, directly call Smartlead API for active (verified) domains
    if (user?.platform === 'Smartlead') {
      // Eligible = Smartlead domains with emails that are NOT verified (pending/unverified)
      const eligibleDomains = domains.filter(d => 
        d.platform === 'Smartlead' &&
        !isEmpty(d.accounts_to_create) &&
        !d.verified_connection &&
        !d.canceled_domain
      );

      // Count total emails for confirmation
      const totalEmails = eligibleDomains.reduce((sum, d) => sum + countEmailsInField(d.accounts_to_create), 0);

      if (eligibleDomains.length === 0 || totalEmails === 0) {
        alert('No eligible Smartlead domains/emails to upload (requires Active and not Pending).');
        return;
      }

      if (!window.confirm(`This will upload OAuth data for ${totalEmails} email(s) across ${eligibleDomains.length} Smartlead domain(s). Continue?`)) {
        return;
      }

      setIsVerifying(true);
      setVerificationResults(null);
      setShowLiveProgress(true);

      setLiveProgress({
        domainsProcessed: 0,
        totalDomains: eligibleDomains.length,
        responses: []
      });

      setLiveProgress(prev => ({
        ...prev,
        responses: [
          ...prev.responses,
          {
            domain: 'System',
            success: true,
            timestamp: new Date().toISOString(),
            response: { message: `🚀 Starting Smartlead oAuth upload for ${eligibleDomains.length} domain(s), ${totalEmails} email(s)` }
          }
        ]
      }));

      try {
        let processedDomains = 0;
        let successCount = 0;
        let failCount = 0;

        for (const d of eligibleDomains) {
          try {
            const result = await apiService.oAuthUploadDomain(d.id);

            if (result.success) {
              successCount += result.successCount || 0;
              failCount += result.failCount || 0;
            } else {
              failCount += (result.totalEmails || 0);
            }

            // Emit per-domain detail into progress
            setLiveProgress(prev => ({
              ...prev,
              responses: [...prev.responses, {
                domain: d.domain_name,
                success: result.success,
                timestamp: new Date().toISOString(),
                response: result
              }]
            }));
          } catch (err) {
            failCount++;
            setLiveProgress(prev => ({
              ...prev,
              responses: [...prev.responses, {
                domain: d.domain_name,
                success: false,
                timestamp: new Date().toISOString(),
                response: { error: err instanceof Error ? err.message : 'Unknown error' }
              }]
            }));
          }

          processedDomains++;
          setLiveProgress(prev => ({ ...prev, domainsProcessed: processedDomains }));
        }

        setIsVerifying(false);
        setLiveProgress(prev => ({
          ...prev,
          responses: [...prev.responses, {
            domain: 'Upload Complete',
            success: true,
            timestamp: new Date().toISOString(),
            response: { message: `🎉 Smartlead oAuth upload complete. Success: ${successCount}, Failed: ${failCount}` }
          }]
        }));

        alert(`Smartlead oAuth upload complete.\nSuccessful uploads: ${successCount}\nFailed uploads: ${failCount}`);
      } catch (error) {
        console.error('Smartlead oAuth upload error:', error);
        setIsVerifying(false);
        setLiveProgress(prev => ({
          ...prev,
          responses: [...prev.responses, {
            domain: 'Upload Failed',
            success: false,
            timestamp: new Date().toISOString(),
            response: { message: `❌ Smartlead oAuth upload failed: ${error instanceof Error ? error.message : 'Unknown error'}` }
          }]
        }));
        alert(`Smartlead oAuth upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      return; // Do not proceed to Instantly flow
    }

    // Default (non-Smartlead) behavior: create background job for pending domains
    const pendingCount = statusCounts.pending;

    if (pendingCount === 0) {
      alert('No pending domains to upload.');
      return;
    }

    if (!window.confirm(`This will upload credentials for ${pendingCount} pending domains to the external oAuth system. This process will continue in the background. Continue?`)) {
      return;
    }

    setIsVerifying(true); // Reusing the verification state for loading
    setVerificationResults(null);
    setShowLiveProgress(true);
    
    // Initialize live progress for oAuth upload
    setLiveProgress({
      domainsProcessed: 0,
      totalDomains: pendingCount,
      responses: []
    });

    // Add initial progress entry
    setLiveProgress(prev => ({
      ...prev,
      responses: [...prev.responses, {
        domain: 'System',
        success: true,
        timestamp: new Date().toISOString(),
        response: { message: `🚀 Starting oAuth upload for ${pendingCount} pending domains...\n\nUploading credentials to external oAuth system` }
      }]
    }));

    try {
      // Create oAuth upload job
      const jobResult = await apiService.createOAuthUploadJob(userId);
      
      if (!jobResult.success) {
        throw new Error(jobResult.message);
      }

      const jobId = jobResult.data.id;
      console.log(`Created oAuth upload job: ${jobId}`);

      // Add job creation success message
      setLiveProgress(prev => ({
        ...prev,
        responses: [...prev.responses, {
          domain: 'Job Created',
          success: true,
          timestamp: new Date().toISOString(),
          response: { message: `✅ Background oAuth upload job created successfully (ID: ${jobId})` }
        }]
      }));

      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusResult = await apiService.getJobStatus(jobId);
          
          if (statusResult.success && statusResult.data) {
            const job = statusResult.data;
            
            // Update progress based on job data
            setLiveProgress(prev => {
              const newProgress = { ...prev };
              
              // Update basic counters
              newProgress.domainsProcessed = job.processed_domains || 0;
              newProgress.totalDomains = job.total_domains || pendingCount;
              
              // Handle progress data for oAuth upload
              if (job.progress_data) {
                const progress = job.progress_data;
                newProgress.currentDomain = progress.currentDomain;
                
                // Add phase-specific progress messages
                let phaseMessage = '';
                switch (progress.phase) {
                  case 'uploading_credentials':
                    phaseMessage = `🔐 Uploading credentials (${progress.domainsProcessed || 0}/${newProgress.totalDomains})`;
                    if (progress.currentDomain && progress.currentDomain !== 'Starting oAuth upload phase') {
                      phaseMessage += `\nCurrently processing: ${progress.currentDomain}`;
                    }
                    break;
                  case 'completed':
                    phaseMessage = `🎉 oAuth upload complete!\nCredentials: ${progress.credentialsUploaded || 0} uploaded, ${progress.credentialsFailed || 0} failed`;
                    break;
                  default:
                    if (progress.currentDomain) {
                      phaseMessage = progress.currentDomain;
                    }
                }
                
                // Only add new progress message if it's different from the last one
                const lastResponse = newProgress.responses[newProgress.responses.length - 1];
                if (!lastResponse || lastResponse.response?.message !== phaseMessage) {
                  newProgress.responses = [...newProgress.responses, {
                    domain: progress.phase || 'Progress Update',
                    success: true,
                    timestamp: new Date().toISOString(),
                    response: { message: phaseMessage }
                  }];
                }
              }
              
              return newProgress;
            });

            // Job completed
            if (job.status === 'completed' || job.status === 'failed') {
              clearInterval(pollInterval);
              setIsVerifying(false);

              if (job.status === 'completed') {
                // Add final success message
                const finalMessage = `🎉 oAuth upload completed successfully!\n\n` +
                  `📊 Final Summary:\n` +
                  `• Credentials Uploaded: ${job.progress_data?.credentialsUploaded || 0}\n` +
                  `• Upload Failures: ${job.progress_data?.credentialsFailed || 0}\n` +
                  `• Total Domains Processed: ${job.processed_domains || 0}`;

                setLiveProgress(prev => ({
                  ...prev,
                  responses: [...prev.responses, {
                    domain: 'Upload Complete',
                    success: true,
                    timestamp: new Date().toISOString(),
                    response: { message: finalMessage }
                  }]
                }));

                alert(`oAuth Upload sent for processing successfully. Please attempt verification after 15 minutes.\n\nCredentials Uploaded: ${job.progress_data?.credentialsUploaded || 0}\nFailed: ${job.progress_data?.credentialsFailed || 0}`);
                
                // Refresh domains data to show any updates
                await fetchDomains();
              } else {
                // Add final error message
                setLiveProgress(prev => ({
                  ...prev,
                  responses: [...prev.responses, {
                    domain: 'Upload Failed',
                    success: false,
                    timestamp: new Date().toISOString(),
                    response: {
                      message: `❌ oAuth upload failed: ${job.error_message || 'Unknown error'}`,
                      error: job.error_message
                    }
                  }]
                }));

                alert(`oAuth upload failed: ${job.error_message || 'Unknown error'}`);
              }
            }
          }
        } catch (error) {
          console.error('Error checking job status:', error);
          setLiveProgress(prev => ({
            ...prev,
            responses: [...prev.responses, {
              domain: 'Status Check Error',
              success: false,
              timestamp: new Date().toISOString(),
              response: { message: `❌ Error checking job status: ${error instanceof Error ? error.message : 'Unknown error'}` }
            }]
          }));
        }
      }, 2000); // Poll every 2 seconds

      // Set a timeout to stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isVerifying) {
          setIsVerifying(false);
          setLiveProgress(prev => ({
            ...prev,
            responses: [...prev.responses, {
              domain: 'Timeout',
              success: false,
              timestamp: new Date().toISOString(),
              response: { message: '⏰ Polling timeout reached. The job may still be running in the background.' }
            }]
          }));
        }
      }, 600000); // 10 minutes

    } catch (error) {
      console.error('Error starting oAuth upload job:', error);
      setIsVerifying(false);
      
      setLiveProgress(prev => ({
        ...prev,
        responses: [...prev.responses, {
          domain: 'Job Creation Error',
          success: false,
          timestamp: new Date().toISOString(),
          response: { message: `❌ Failed to create oAuth upload job: ${error instanceof Error ? error.message : 'Unknown error'}` }
        }]
      }));
      
      alert(`Failed to start oAuth upload: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handler for Verify button
  const handleVerifyConnections = async () => {
    if (!userId || isVerifying) return;
    
    const pendingCount = statusCounts.pending;
    
    if (pendingCount === 0) {
      alert('No pending domains to verify.');
      return;
    }

    if (!window.confirm(`This will first send passwords and then verify ${pendingCount} pending domains. This process might take a while and will continue in the background. Continue?`)) {
      return;
    }

    setIsVerifying(true);
    setVerificationResults(null);
    setShowLiveProgress(true);
    
    // Initialize live progress
    setLiveProgress({
      domainsProcessed: 0,
      totalDomains: pendingCount,
      responses: []
    });

    // Add initial progress entry
    setLiveProgress(prev => ({
      ...prev,
      responses: [...prev.responses, {
        domain: 'System',
        success: true,
        timestamp: new Date().toISOString(),
        response: { message: `🚀 Starting bulk process for ${pendingCount} pending domains...\n\nPhase 1: Sending passwords for active domains\nPhase 2: Verifying domain connections` }
      }]
    }));

    try {
      // Create verification job (which now includes password sending)
      const jobResult = await apiService.createVerificationJob(userId);
      
      if (!jobResult.success) {
        throw new Error(jobResult.message);
      }

      const jobId = jobResult.data.id;
      console.log(`Created verification job: ${jobId}`);

      // Add job creation success message
      setLiveProgress(prev => ({
        ...prev,
        responses: [...prev.responses, {
          domain: 'Job Created',
          success: true,
          timestamp: new Date().toISOString(),
          response: { message: `✅ Background job created successfully (ID: ${jobId})` }
        }]
      }));

      // Poll for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusResult = await apiService.getJobStatus(jobId);
          
          if (statusResult.success && statusResult.data) {
            const job = statusResult.data;
            
            // Update progress based on job data
            setLiveProgress(prev => {
              const newProgress = { ...prev };
              
              // Update basic counters
              newProgress.domainsProcessed = job.processed_domains || 0;
              newProgress.totalDomains = job.total_domains || pendingCount;
              
              // Handle progress data for different phases
              if (job.progress_data) {
                const progress = job.progress_data;
                newProgress.currentDomain = progress.currentDomain;
                
                // Add phase-specific progress messages
                let phaseMessage = '';
                switch (progress.phase) {
                  case 'sending_passwords':
                    phaseMessage = `🔐 Phase 1: Sending passwords (${progress.domainsProcessed || 0}/${newProgress.totalDomains})`;
                    if (progress.currentDomain && progress.currentDomain !== 'Starting password sending phase') {
                      phaseMessage += `\nCurrently processing: ${progress.currentDomain}`;
                    }
                    break;
                  case 'passwords_complete':
                    phaseMessage = `✅ Phase 1 Complete: ${progress.passwordsSent || 0} domains sent passwords, ${progress.passwordsFailed || 0} failed\n🔍 Phase 2: Starting verification...`;
                    break;
                  case 'verifying_connections':
                    phaseMessage = `🔍 Phase 2: Verifying connections (${newProgress.domainsProcessed}/${newProgress.totalDomains})`;
                    if (progress.currentDomain) {
                      phaseMessage += `\nCurrently verifying: ${progress.currentDomain}`;
                    }
                    if (progress.passwordsSent !== undefined) {
                      phaseMessage += `\nPasswords sent in Phase 1: ${progress.passwordsSent}/${newProgress.totalDomains}`;
                    }
                    break;
                  case 'completed':
                    phaseMessage = `🎉 All phases complete!\nPasswords: ${progress.passwordsSent || 0} sent, ${progress.passwordsFailed || 0} failed\nVerification: ${job.successful_domains || 0} successful, ${job.failed_domains || 0} failed`;
                    break;
                  default:
                    if (progress.currentDomain) {
                      phaseMessage = progress.currentDomain;
                    }
                }
                
                // Only add new progress message if it's different from the last one
                const lastResponse = newProgress.responses[newProgress.responses.length - 1];
                if (!lastResponse || lastResponse.response?.message !== phaseMessage) {
                  newProgress.responses = [...newProgress.responses, {
                    domain: progress.phase || 'Progress Update',
                    success: true,
                    timestamp: new Date().toISOString(),
                    response: { message: phaseMessage }
                  }];
                }
              }
              
              return newProgress;
            });

            // Job completed
            if (job.status === 'completed' || job.status === 'failed') {
              clearInterval(pollInterval);
              setIsVerifying(false);

              if (job.status === 'completed') {
                // Add final success message
                const finalMessage = `🎉 Bulk process completed successfully!\n\n` +
                  `📊 Final Summary:\n` +
                  `• Password Phase: ${job.progress_data?.passwordsSent || 0} sent, ${job.progress_data?.passwordsFailed || 0} failed\n` +
                  `• Verification Phase: ${job.successful_domains || 0} successful, ${job.failed_domains || 0} failed\n` +
                  `• Total Domains Processed: ${job.processed_domains || 0}`;

                setLiveProgress(prev => ({
                  ...prev,
                  responses: [...prev.responses, {
                    domain: 'Process Complete',
                    success: true,
                    timestamp: new Date().toISOString(),
                    response: { message: finalMessage }
                  }]
                }));

                alert(`Bulk process completed!\n\nPassword Sending: ${job.progress_data?.passwordsSent || 0} sent, ${job.progress_data?.passwordsFailed || 0} failed\nVerification: ${job.successful_domains || 0} successful, ${job.failed_domains || 0} failed`);
                
                // Refresh domains data to show updated verification results
                await fetchDomains();
              } else {
                // Add final error message
                setLiveProgress(prev => ({
                  ...prev,
                  responses: [...prev.responses, {
                    domain: 'Process Failed',
                    success: false,
                    timestamp: new Date().toISOString(),
                    response: {
                      message: `❌ Bulk process failed: ${job.error_message || 'Unknown error'}`,
                      error: job.error_message
                    }
                  }]
                }));

                alert(`Bulk process failed: ${job.error_message || 'Unknown error'}`);
              }
            }
          }
        } catch (error) {
          console.error('Error checking job status:', error);
          setLiveProgress(prev => ({
            ...prev,
            responses: [...prev.responses, {
              domain: 'Status Check Error',
              success: false,
              timestamp: new Date().toISOString(),
              response: { message: `❌ Error checking job status: ${error instanceof Error ? error.message : 'Unknown error'}` }
            }]
          }));
        }
      }, 2000); // Poll every 2 seconds

      // Set a timeout to stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isVerifying) {
          setIsVerifying(false);
          setLiveProgress(prev => ({
            ...prev,
            responses: [...prev.responses, {
              domain: 'Timeout',
              success: false,
              timestamp: new Date().toISOString(),
              response: { message: '⏰ Polling timeout reached. The job may still be running in the background.' }
            }]
          }));
        }
      }, 600000); // 10 minutes

    } catch (error) {
      console.error('Error starting verification job:', error);
      setIsVerifying(false);
      
      setLiveProgress(prev => ({
        ...prev,
        responses: [...prev.responses, {
          domain: 'Job Creation Error',
          success: false,
          timestamp: new Date().toISOString(),
          response: { message: `❌ Failed to create verification job: ${error instanceof Error ? error.message : 'Unknown error'}` }
        }]
      }));
      
      alert(`Failed to start bulk process: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handler for Copy CSV button
  const handleCopyCSV = () => {
    // Filter pending domains
    const pendingDomains = domains.filter(d => 
      !d.verified_connection && 
      !d.canceled_domain
    );
    
    if (pendingDomains.length === 0) {
      alert('No pending domains found to export');
      return;
    }

    // Generate CSV data
    const csvRows = [];
    
    // Add header (using tabs instead of commas for better Google Sheets compatibility)
    csvRows.push('Domain\tEmail\tPassword\tFirst Name\tLast Name');
    
    for (const domain of pendingDomains) {
      // Parse emails from accounts_to_create
      let emails = [];
      try {
        if (domain.accounts_to_create) {
          const parsed = JSON.parse(domain.accounts_to_create);
          if (Array.isArray(parsed)) {
            emails = parsed;
          } else if (typeof parsed === 'object') {
            emails = Object.values(parsed).filter(v => typeof v === 'string');
          } else if (typeof parsed === 'string') {
            emails = [parsed];
          }
        }
      } catch {
        // If not JSON, try splitting by comma
        if (domain.accounts_to_create) {
          emails = domain.accounts_to_create
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0 && email.includes('@'));
        }
      }

      // Parse names from names field
      let names = [];
      try {
        if (domain.names) {
          const parsed = JSON.parse(domain.names);
          if (Array.isArray(parsed)) {
            names = parsed;
          } else if (typeof parsed === 'object') {
            names = Object.values(parsed).filter(v => typeof v === 'string');
          } else if (typeof parsed === 'string') {
            names = [parsed];
          }
        }
      } catch {
        // If not JSON, try splitting by comma
        if (domain.names) {
          names = domain.names
            .split(',')
            .map(name => name.trim())
            .filter(name => name.length > 0);
        }
      }

      // If no emails, skip this domain
      if (emails.length === 0) {
        continue;
      }

      // Smart matching function to pair emails with names
      const matchEmailToName = (email: string, namesList: string[]) => {
        // Extract the local part of the email (before @)
        const localPart = email.split('@')[0].toLowerCase();
        
        // Try to find the best matching name
        let bestMatch = '';
        let bestScore = 0;
        
        for (const name of namesList) {
          const nameLower = name.toLowerCase();
          const nameParts = nameLower.split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts[nameParts.length - 1] || '';
          
          let score = 0;
          
          // Direct firstname match (e.g., lily.albrecht -> Lily)
          if (firstName && localPart.includes(firstName)) {
            score += 50;
          }
          
          // Direct lastname match (e.g., lily.albrecht -> Albrecht)
          if (lastName && localPart.includes(lastName)) {
            score += 50;
          }
          
          // First initial + lastname (e.g., laurelc -> Laurel C...)
          if (firstName && lastName && localPart.startsWith(firstName.charAt(0)) && localPart.includes(lastName.charAt(0))) {
            score += 30;
          }
          
          // First initial + full lastname (e.g., emendes -> e + mendes)
          if (firstName && lastName && 
              localPart.startsWith(firstName.charAt(0)) && 
              localPart.slice(1).includes(lastName)) {
            score += 40;
          }
          
          // Initials matching (e.g., jsmith -> John Smith)
          if (firstName && lastName && localPart.length >= 2) {
            const emailInitials = localPart.charAt(0) + localPart.charAt(1);
            const nameInitials = firstName.charAt(0) + lastName.charAt(0);
            if (emailInitials === nameInitials) {
              score += 25;
            }
          }
          
          // Full name concatenated (e.g., johnsmith -> John Smith)
          const fullNameConcatenated = nameParts.join('');
          if (fullNameConcatenated && localPart.includes(fullNameConcatenated)) {
            score += 60;
          }
          
          // Reverse: lastname.firstname (e.g., smith.john -> John Smith)
          if (firstName && lastName && localPart.includes(`${lastName}.${firstName}`)) {
            score += 60;
          }
          
          if (score > bestScore) {
            bestScore = score;
            bestMatch = name;
          }
        }
        
        return bestMatch || namesList[0] || ''; // Fallback to first name or empty
      };

      // Create a copy of names to track which ones we've used
      const availableNames = [...names];
      const usedNames = new Set();

      // Create CSV rows for each email with smart name matching
      for (const email of emails) {
        // Find the best matching name for this email
        const matchedName = matchEmailToName(email, availableNames.filter(n => !usedNames.has(n)));
        
        // If we found a good match, mark it as used so it won't be reused
        if (matchedName && !usedNames.has(matchedName)) {
          usedNames.add(matchedName);
        }
        
        // Split name into first and last name
        const nameParts = matchedName.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Clean values (remove tabs and newlines that could break formatting)
        const cleanValue = (value: string | undefined | null) => {
          if (!value) return '';
          return String(value).replace(/[\t\n\r]/g, ' ').trim();
        };

        // Use tab-separated values for better Google Sheets compatibility
        const csvRow = [
          cleanValue(domain.domain_name),
          cleanValue(email),
          cleanValue(formatPassword(domain)),
          cleanValue(firstName),
          cleanValue(lastName)
        ].join('\t');
        
        csvRows.push(csvRow);
      }
    }

    // Join all rows with newlines
    const csvContent = csvRows.join('\n');
    
    // Copy to clipboard
    navigator.clipboard.writeText(csvContent).then(() => {
      alert(`Data copied to clipboard!\n\n${csvRows.length - 1} rows exported from ${pendingDomains.length} pending domains.\n\nPaste directly into Google Sheets - columns will separate automatically.`);
    }).catch((error) => {
      console.error('Failed to copy to clipboard:', error);
      // Fallback: show the CSV content in a text area for manual copying
      const textarea = document.createElement('textarea');
      textarea.value = csvContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert(`Data copied to clipboard!\n\n${csvRows.length - 1} rows exported from ${pendingDomains.length} pending domains.\n\nPaste directly into Google Sheets - columns will separate automatically.`);
    });
  };

  if (userLoading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-destructive">{userError || 'User not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary px-4 py-2 rounded-lg font-medium"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in pt-12 lg:pt-0">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Users
      </button>

      {/* User Details */}
      <div className="card-modern overflow-hidden">
        <div className="px-6 py-5 border-b border-border">
          <h3 className="text-lg font-semibold">User Information</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Personal details and application information
          </p>
        </div>
        <div className="p-0">
          <dl className="divide-y divide-border">
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Email</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2 break-all">{user.email}</dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Client ID</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2 font-mono break-all">{user.client_id}</dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Platform</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
                  {user.platform}
                </span>
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <select
                  className="input-modern h-8 px-2 text-xs min-w-[220px]"
                  value={user.order_status || ''}
                  onChange={(e) => handleOrderStatusChange(e.target.value)}
                  disabled={updatingStatus}
                >
                  {ORDER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {updatingStatus && (
                  <div className="mt-1 text-xs text-muted-foreground">Updating...</div>
                )}
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Number of Domains</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
                  {totalDomains}
                </span>
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Pending Domains</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  pendingDomains > 0 
                    ? 'bg-amber-500/10 text-amber-500' 
                    : 'bg-green-500/10 text-green-500'
                }`}>
                  {pendingDomains}
                </span>
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Workspace ID</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2 font-mono break-all">
                {user.workspace_id || '-'}
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Admin Notes</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <div className="flex items-start justify-between">
                  <p className="whitespace-pre-wrap text-sm break-words flex-1 pr-4">
                    {user.admin_notes || <span className="text-muted-foreground">No admin notes</span>}
                  </p>
                  <button
                    className="btn-outline px-3 py-1 text-xs rounded-md hover:bg-muted transition-colors flex-shrink-0"
                    onClick={handleNotesModalOpen}
                    title="Edit notes"
                  >
                    Edit
                  </button>
                </div>
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Client Notes</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <div className="flex items-start justify-between">
                  <p className="whitespace-pre-wrap text-sm break-words flex-1 pr-4">
                    {user.client_notes || <span className="text-muted-foreground">No client notes</span>}
                  </p>
                  <button
                    className="btn-outline px-3 py-1 text-xs rounded-md hover:bg-muted transition-colors flex-shrink-0"
                    onClick={handleNotesModalOpen}
                    title="Edit notes"
                  >
                    Edit
                  </button>
                </div>
              </dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">{formatDate(user.created_at)}</dd>
            </div>
            <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">{user.updated_at ? formatDate(user.updated_at) : '-'}</dd>
            </div>
            {currentUser?.userType === 'superadmin' && (
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Generate Invoice</dt>
                <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={generateInvoicePDF}
                      className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Generate PDF Invoice
                    </button>
                    <div className="text-xs text-muted-foreground">
                      <div>• {calculateTotalEmailAccounts()} email accounts × $4 = ${(calculateTotalEmailAccounts() * 4).toFixed(2)}</div>
                      <div>• {calculateCurrentMonthDomains()} current month domains × $12 = ${(calculateCurrentMonthDomains() * 12).toFixed(2)}</div>
                      <div className="font-medium">Total: ${(calculateTotalEmailAccounts() * 4 + calculateCurrentMonthDomains() * 12).toFixed(2)}</div>
                    </div>
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* User Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-modern p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-muted-foreground">Total Domains</div>
              <div className="text-2xl font-bold text-foreground">{userStats.totalDomains}</div>
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-muted-foreground">Total Emails</div>
              <div className="text-2xl font-bold text-foreground">{userStats.totalEmails}</div>
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-muted-foreground">Active Domains</div>
              <div className="text-2xl font-bold text-foreground">{userStats.activeDomainsCount}</div>
            </div>
          </div>
        </div>

        <div className="card-modern p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-muted-foreground">Active Emails</div>
              <div className="text-2xl font-bold text-foreground">{userStats.activeEmailsCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Domains Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Domains</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              All domains associated with this user
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {paginatedDomains.length} of {filteredDomains.length} filtered domains ({totalDomains} total)
          </div>
        </div>

        {/* Domain Filters */}
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/20 rounded-lg">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </div>
            <input
              type="text"
              className="input-modern h-9 w-full pl-9 pr-3 text-sm"
              placeholder="Search domains or emails..."
              value={domainSearch}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="input-modern h-9 px-3 text-sm min-w-[160px]"
            value={domainStatusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {DOMAIN_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {option.value === 'pending' && ` (${statusCounts.pending})`}
                {option.value === 'verified' && ` (${statusCounts.verified})`}
                {option.value === 'canceled' && ` (${statusCounts.canceled})`}
                {option.value === 'all' && ` (${totalDomains})`}
              </option>
            ))}
          </select>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleCopyCSV}
              className="h-9 px-4 text-sm font-medium rounded-lg flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              <Copy className="h-4 w-4" />
              <span>Copy CSV</span>
            </button>
            
            <button
              onClick={handleOAuthUpload}
              className="h-9 px-4 text-sm font-medium rounded-lg flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white transition-colors"
            >
              <Upload className="h-4 w-4" />
              <span>oAuth Upload</span>
            </button>
            
            <button
              onClick={handleVerifyConnections}
              disabled={isVerifying}
              className="h-9 px-4 text-sm font-medium rounded-lg flex items-center space-x-2 disabled:opacity-50 bg-green-600 hover:bg-green-700 text-white"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Send Passwords & Verify</span>
                </>
              )}
            </button>
            
            {/* Show Live Progress Button */}
            {!showLiveProgress && liveProgress.responses.length > 0 && (
              <button
                onClick={() => setShowLiveProgress(true)}
                className="btn-secondary h-9 px-4 text-sm font-medium rounded-lg flex items-center space-x-2"
              >
                <Search className="h-4 w-4" />
                <span>Show Progress ({liveProgress.responses.length})</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Live Progress Section */}
        {showLiveProgress && (
          <div className="card-modern p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isVerifying ? 'bg-blue-500 animate-pulse' : 'bg-green-500'}`}></div>
                <h3 className="font-medium text-blue-700 dark:text-blue-300">
                  {isVerifying ? 'Verification in Progress' : 'Verification Complete'}
                </h3>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  {liveProgress.domainsProcessed} / {liveProgress.totalDomains} domains processed
                </span>
                <button
                  onClick={() => setShowLiveProgress(false)}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  Hide
                </button>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {liveProgress.responses.map((response, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${
                    response.success 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm">
                          {response.domain}
                        </span>
                        {response.email && (
                          <span className="text-xs text-muted-foreground">
                            • {response.email}
                          </span>
                        )}
                        <span className={`w-2 h-2 rounded-full ${
                          response.success ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                      </div>
                      <div className="mt-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                        {JSON.stringify(response.response, null, 2)}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(response.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            {liveProgress.responses.length === 0 && (
              <div className="text-center py-4 text-blue-600 dark:text-blue-400">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                <p className="text-sm">Waiting for verification to start...</p>
              </div>
            )}
          </div>
        )}
        
        <DomainsTable 
          domains={paginatedDomains} 
          loading={domainsLoading} 
          error={domainsError} 
        />

        {/* Pagination */}
        {!domainsLoading && !domainsError && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="relative ml-3 inline-flex items-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 transition-colors"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                  <span className="font-medium text-foreground">
                    {Math.min(pagination.page * pagination.limit, filteredDomains.length)}
                  </span>{' '}
                  of <span className="font-medium text-foreground">{filteredDomains.length}</span> filtered results
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-lg shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center rounded-l-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted focus:z-20 disabled:opacity-50 transition-colors"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold border border-border bg-card">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className="relative inline-flex items-center rounded-r-lg px-2 py-2 text-muted-foreground border border-border hover:bg-muted focus:z-20 disabled:opacity-50 transition-colors"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-card rounded-lg shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold">Notes</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {user.email}
                </p>
              </div>
              <button
                onClick={handleNotesModalClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              {/* Client Notes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Client Notes
                </label>
                <textarea
                  className="input-modern w-full h-32 px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="Add notes about this client..."
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  style={{ resize: 'vertical', minHeight: '120px' }}
                  autoFocus
                />
              </div>

              {/* Admin Notes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Admin Notes
                </label>
                <textarea
                  className="input-modern w-full h-32 px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="Internal admin notes..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  style={{ resize: 'vertical', minHeight: '120px' }}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
              <button
                onClick={handleNotesModalClose}
                className="px-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleNotesSave}
                disabled={updatingNotes}
                className="btn-primary px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 min-w-[100px]"
              >
                {updatingNotes ? (
                  <div className="inline-flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Notes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 